// Page Prestations Mobile - Recherche de services/prestations
// Même design que Acheter à un pro, adapté aux prestations
// Header: [Prestations] [🔍]
// Onglets: Tous | Filtre | Carte | Devis gratuit

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Star, X, Clock, Loader2, MapPin, Phone, ChevronRight,
  SlidersHorizontal, Grid3X3, Map, FileText, Wrench, Zap, Droplets,
  Flame, Home, Brush, Hammer, Shield, Users, Euro, Award, CheckCircle,
  Building2, Waves, TreeDeciduous, Wind, Ruler, ArrowLeft, Filter
} from 'lucide-react';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';
import { useDemoMode } from '../../components/demo/DemoModeProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de prestations / métiers
const METIERS = [
  { id: 'plomberie', label: 'Plomberie', icon: Droplets, color: '#0EA5E9' },
  { id: 'electricite', label: 'Électricité', icon: Zap, color: '#F59E0B' },
  { id: 'maconnerie', label: 'Maçonnerie', icon: Building2, color: '#6B7280' },
  { id: 'carrelage', label: 'Carrelage', icon: Ruler, color: '#10B981' },
  { id: 'peinture', label: 'Peinture', icon: Brush, color: '#EC4899' },
  { id: 'menuiserie', label: 'Menuiserie', icon: Hammer, color: '#92400E' },
  { id: 'couverture', label: 'Couverture', icon: Home, color: '#7C3AED' },
  { id: 'chauffage', label: 'Chauffage', icon: Flame, color: '#EF4444' },
  { id: 'piscine', label: 'Piscine', icon: Waves, color: '#3B82F6' },
  { id: 'jardinage', label: 'Jardinage', icon: TreeDeciduous, color: '#22C55E' },
  { id: 'climatisation', label: 'Climatisation', icon: Wind, color: '#06B6D4' },
  { id: 'serrurerie', label: 'Serrurerie', icon: Wrench, color: '#64748B' },
];

// Toutes les prestations disponibles pour le filtre (extraites de tous les métiers)
const TOUTES_PRESTATIONS = [
  // Plomberie
  { id: 'depannage-plomberie', label: 'Dépannage plomberie', metier: 'plomberie' },
  { id: 'fuite-eau', label: 'Réparation fuite', metier: 'plomberie' },
  { id: 'chauffe-eau', label: 'Chauffe-eau', metier: 'plomberie' },
  { id: 'debouchage', label: 'Débouchage', metier: 'plomberie' },
  { id: 'salle-de-bain', label: 'Salle de bain', metier: 'plomberie' },
  { id: 'robinetterie', label: 'Robinetterie', metier: 'plomberie' },
  // Électricité
  { id: 'tableau-electrique', label: 'Tableau électrique', metier: 'electricite' },
  { id: 'mise-aux-normes', label: 'Mise aux normes', metier: 'electricite' },
  { id: 'depannage-elec', label: 'Dépannage électrique', metier: 'electricite' },
  { id: 'eclairage', label: 'Éclairage LED', metier: 'electricite' },
  { id: 'prises', label: 'Prises / interrupteurs', metier: 'electricite' },
  { id: 'borne-recharge', label: 'Borne recharge VE', metier: 'electricite' },
  // Carrelage
  { id: 'carrelage-sol', label: 'Carrelage sol', metier: 'carrelage' },
  { id: 'carrelage-mural', label: 'Carrelage mural', metier: 'carrelage' },
  { id: 'faience', label: 'Faïence', metier: 'carrelage' },
  { id: 'terrasse', label: 'Terrasse extérieure', metier: 'carrelage' },
  // Peinture
  { id: 'peinture-interieure', label: 'Peinture intérieure', metier: 'peinture' },
  { id: 'peinture-facade', label: 'Peinture façade', metier: 'peinture' },
  { id: 'ravalement', label: 'Ravalement', metier: 'peinture' },
  { id: 'papier-peint', label: 'Papier peint', metier: 'peinture' },
  { id: 'enduit', label: 'Enduit / rebouchage', metier: 'peinture' },
  // Maçonnerie
  { id: 'construction-mur', label: 'Construction mur', metier: 'maconnerie' },
  { id: 'demolition', label: 'Démolition', metier: 'maconnerie' },
  { id: 'dalle-beton', label: 'Dalle béton', metier: 'maconnerie' },
  { id: 'ouverture-mur', label: 'Ouverture mur porteur', metier: 'maconnerie' },
  // Menuiserie
  { id: 'pose-fenetre', label: 'Pose fenêtres', metier: 'menuiserie' },
  { id: 'porte-interieure', label: 'Portes intérieures', metier: 'menuiserie' },
  { id: 'porte-entree', label: 'Porte d\'entrée', metier: 'menuiserie' },
  { id: 'placards', label: 'Placards sur mesure', metier: 'menuiserie' },
  { id: 'terrasse-bois', label: 'Terrasse bois', metier: 'menuiserie' },
  // Chauffage
  { id: 'chaudiere', label: 'Installation chaudière', metier: 'chauffage' },
  { id: 'pompe-chaleur', label: 'Pompe à chaleur', metier: 'chauffage' },
  { id: 'radiateurs', label: 'Radiateurs', metier: 'chauffage' },
  { id: 'plancher-chauffant', label: 'Plancher chauffant', metier: 'chauffage' },
  { id: 'entretien-chaudiere', label: 'Entretien chaudière', metier: 'chauffage' },
  // Couverture
  { id: 'toiture', label: 'Réfection toiture', metier: 'couverture' },
  { id: 'fuite-toiture', label: 'Réparation fuite toit', metier: 'couverture' },
  { id: 'gouttiere', label: 'Gouttières', metier: 'couverture' },
  { id: 'velux', label: 'Velux / fenêtre toit', metier: 'couverture' },
  { id: 'isolation-toiture', label: 'Isolation toiture', metier: 'couverture' },
  // Climatisation
  { id: 'installation-clim', label: 'Installation clim', metier: 'climatisation' },
  { id: 'entretien-clim', label: 'Entretien clim', metier: 'climatisation' },
  { id: 'depannage-clim', label: 'Dépannage clim', metier: 'climatisation' },
  // Serrurerie
  { id: 'ouverture-porte', label: 'Ouverture porte', metier: 'serrurerie' },
  { id: 'changement-serrure', label: 'Changement serrure', metier: 'serrurerie' },
  { id: 'blindage-porte', label: 'Blindage porte', metier: 'serrurerie' },
];

// Types de tarifs
const TARIF_TYPES = [
  { id: 'forfait', label: 'Forfait', color: '#22C55E' },
  { id: 'horaire', label: 'Horaire', color: '#3B82F6' },
  { id: 'metre_carre', label: 'Au m²', color: '#06B6D4' },
  { id: 'sur_devis', label: 'Sur devis', color: '#8B5CF6' },
  { id: 'gratuit', label: 'Gratuit', color: '#F97316' },
];

// Mock data prestations
const MOCK_PRESTATIONS = [
  {
    id: 'presta-1',
    name: 'Réparation fuite d\'eau',
    description: 'Intervention rapide pour tous types de fuites. Détection, réparation, remplacement de joints et raccords.',
    metier: 'plomberie',
    tarif_type: 'forfait',
    tarif_montant: 80,
    tarif_unite: '€',
    duration: '1-2h',
    artisan: {
      id: 'artisan-1',
      shop_name: 'Plomberie Martin & Fils',
      city: 'Lyon',
      rating: 4.9,
      reviews_count: 127,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=800',
    urgent_available: true,
  },
  {
    id: 'presta-2',
    name: 'Installation tableau électrique',
    description: 'Mise aux normes NF C 15-100. Installation, remplacement ou extension de tableau électrique.',
    metier: 'electricite',
    tarif_type: 'sur_devis',
    tarif_montant: null,
    tarif_unite: '€',
    duration: '4-8h',
    artisan: {
      id: 'artisan-2',
      shop_name: 'Élec Pro Méditerranée',
      city: 'Marseille',
      rating: 4.8,
      reviews_count: 89,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800',
    urgent_available: false,
  },
  {
    id: 'presta-3',
    name: 'Pose de carrelage',
    description: 'Pose de carrelage sol et mur. Tous formats, tous styles. Préparation du support incluse.',
    metier: 'carrelage',
    tarif_type: 'metre_carre',
    tarif_montant: 45,
    tarif_unite: '€/m²',
    duration: '2-5 jours',
    artisan: {
      id: 'artisan-3',
      shop_name: 'Carrelage Expert',
      city: 'Toulouse',
      rating: 4.7,
      reviews_count: 64,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    urgent_available: false,
  },
  {
    id: 'presta-4',
    name: 'Peinture intérieure',
    description: 'Préparation des surfaces, application de 2 couches, finition soignée. Peinture écologique disponible.',
    metier: 'peinture',
    tarif_type: 'metre_carre',
    tarif_montant: 25,
    tarif_unite: '€/m²',
    duration: '2-4 jours',
    artisan: {
      id: 'artisan-4',
      shop_name: 'Peinture Leroy',
      city: 'Paris',
      rating: 4.9,
      reviews_count: 203,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800',
    urgent_available: true,
  },
  {
    id: 'presta-5',
    name: 'Dépannage électrique',
    description: 'Intervention rapide 7j/7 pour panne, court-circuit, disjoncteur. Diagnostic offert.',
    metier: 'electricite',
    tarif_type: 'horaire',
    tarif_montant: 55,
    tarif_unite: '€/h',
    duration: '1-3h',
    artisan: {
      id: 'artisan-2',
      shop_name: 'Élec Pro Méditerranée',
      city: 'Marseille',
      rating: 4.8,
      reviews_count: 89,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=800',
    urgent_available: true,
  },
];

// Carte Prestation
function PrestationCard({ prestation, onClick }) {
  const metier = METIERS.find(m => m.id === prestation.metier);
  const MetierIcon = metier?.icon || Wrench;
  const tarifType = TARIF_TYPES.find(t => t.id === prestation.tarif_type);

  const formatPrice = () => {
    if (prestation.tarif_type === 'sur_devis') return 'Sur devis';
    if (prestation.tarif_type === 'gratuit') return 'Gratuit';
    if (prestation.tarif_montant) {
      // Afficher avec l'unité appropriée
      const unite = prestation.tarif_unite || '€';
      if (prestation.tarif_type === 'horaire' || unite === '€/h') {
        return `${prestation.tarif_montant}€/h`;
      }
      if (prestation.tarif_type === 'metre_carre' || unite === '€/m²') {
        return `${prestation.tarif_montant}€/m²`;
      }
      if (unite === '€/ml') {
        return `${prestation.tarif_montant}€/ml`;
      }
      // Forfait par défaut
      return `À partir de ${prestation.tarif_montant}€`;
    }
    return 'Sur devis';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer"
      onClick={() => onClick(prestation)}
      data-testid="prestation-card"
    >
      {/* Image */}
      <div className="relative aspect-[16/10]">
        {prestation.image ? (
          <img 
            src={prestation.image} 
            alt={prestation.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <MetierIcon size={32} className="text-gray-300" />
          </div>
        )}
        
        {/* Badge Métier */}
        <div 
          className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 text-white"
          style={{ backgroundColor: metier?.color || '#6B7280' }}
        >
          <MetierIcon size={10} />
          {metier?.label || 'Service'}
        </div>
        
        {/* Badge Urgent disponible */}
        {prestation.urgent_available && (
          <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-500 text-white">
            Urgent dispo
          </div>
        )}
        
        {/* Prix */}
        <div className="absolute bottom-2 right-2 px-2 py-1 rounded-lg bg-white/95 shadow-sm">
          <span className="text-sm font-bold text-gray-900">{formatPrice()}</span>
        </div>
      </div>
      
      {/* Contenu */}
      <div className="p-3">
        <h3 className="font-bold text-gray-900 text-sm line-clamp-1">{prestation.name}</h3>
        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{prestation.description}</p>
        
        {/* Artisan */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-full bg-[#1E3A5F] flex items-center justify-center">
              <Building2 size={12} className="text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-900 line-clamp-1">{prestation.artisan.shop_name}</p>
              <div className="flex items-center gap-1">
                <MapPin size={9} className="text-gray-400" />
                <span className="text-[10px] text-gray-500">{prestation.artisan.city}</span>
              </div>
            </div>
          </div>
          
          {/* Rating */}
          <div className="flex items-center gap-0.5">
            <Star size={12} className="text-yellow-400 fill-yellow-400" />
            <span className="text-xs font-medium text-gray-700">{prestation.artisan.rating}</span>
            <span className="text-[10px] text-gray-400">({prestation.artisan.reviews_count})</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function MobilePrestationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requireAuth } = useDemoMode();
  
  // États
  const [prestations, setPrestations] = useState([]);
  const [totalPrestations, setTotalPrestations] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeTab, setActiveTab] = useState('tous');
  
  // Filtres
  const [showFullFilters, setShowFullFilters] = useState(false);
  const [selectedPrestation, setSelectedPrestation] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState({});
  
  // Appliquer les filtres avancés
  const handleAdvancedFiltersChange = (newFilters) => {
    setAdvancedFilters(newFilters);
  };
  
  const handleApplyAdvancedFilters = () => {
    setShowFullFilters(false);
    // Construire la recherche basée sur les filtres
    let searchTerm = null;
    
    // Si un type de prestation est sélectionné, chercher par son label
    if (advancedFilters.type_prestation) {
      const presta = TOUTES_PRESTATIONS.find(p => p.id === advancedFilters.type_prestation);
      if (presta) searchTerm = presta.label;
    }
    
    // Recharger avec les filtres
    fetchPrestationsWithFilters(searchTerm, advancedFilters);
  };
  
  // Charger les prestations avec filtres avancés
  const fetchPrestationsWithFilters = async (search = null, filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.tarification) params.append('tarif_type', filters.tarification);
      if (filters.urgence === 'urgent') params.append('urgent_only', 'true');
      params.append('per_page', '50');
      
      const response = await fetch(`${API_URL}/api/artisans/prestations?${params}`);
      const data = await response.json();
      
      if (data.prestations && data.prestations.length > 0) {
        let filtered = data.prestations;
        
        // Filtrer côté client si nécessaire
        if (filters.tarification) {
          filtered = filtered.filter(p => p.tarif_type === filters.tarification);
        }
        if (filters.urgence === 'urgent') {
          filtered = filtered.filter(p => p.urgent_available);
        }
        
        setPrestations(filtered);
        setTotalPrestations(filtered.length);
      } else {
        // Fallback aux données démo - avec filtrage local
        let filtered = [...MOCK_PRESTATIONS];
        
        if (search) {
          const searchLower = search.toLowerCase();
          filtered = filtered.filter(p => 
            p.name.toLowerCase().includes(searchLower) ||
            p.description.toLowerCase().includes(searchLower)
          );
        }
        
        // Appliquer les filtres avancés
        if (filters.tarification) {
          filtered = filtered.filter(p => p.tarif_type === filters.tarification);
        }
        if (filters.urgence === 'urgent') {
          filtered = filtered.filter(p => p.urgent_available);
        }
        
        setPrestations(filtered);
        setTotalPrestations(filtered.length);
      }
    } catch (error) {
      console.error('Error fetching prestations:', error);
      // Fallback aux données démo
      let filtered = [...MOCK_PRESTATIONS];
      
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(p => 
          p.name.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
        );
      }
      
      if (filters.tarification) {
        filtered = filtered.filter(p => p.tarif_type === filters.tarification);
      }
      if (filters.urgence === 'urgent') {
        filtered = filtered.filter(p => p.urgent_available);
      }
      
      setPrestations(filtered);
      setTotalPrestations(filtered.length);
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les prestations depuis l'API
  const fetchPrestations = async (search = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('per_page', '50');
      
      const response = await fetch(`${API_URL}/api/artisans/prestations?${params}`);
      const data = await response.json();
      
      if (data.prestations && data.prestations.length > 0) {
        setPrestations(data.prestations);
        setTotalPrestations(data.total);
      } else {
        // Fallback aux données démo si base vide - avec filtrage local
        let filtered = [...MOCK_PRESTATIONS];
        
        // Filtrer par recherche (nom de prestation)
        if (search) {
          const searchLower = search.toLowerCase();
          filtered = filtered.filter(p => 
            p.name.toLowerCase().includes(searchLower) ||
            p.description.toLowerCase().includes(searchLower)
          );
        }
        
        setPrestations(filtered);
        setTotalPrestations(filtered.length);
      }
    } catch (error) {
      console.error('Error fetching prestations:', error);
      // Fallback aux données démo en cas d'erreur
      let filtered = [...MOCK_PRESTATIONS];
      
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(p => 
          p.name.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
        );
      }
      
      setPrestations(filtered);
      setTotalPrestations(filtered.length);
    } finally {
      setLoading(false);
    }
  };
  
  // Charger au démarrage
  useEffect(() => {
    fetchPrestations(searchQuery || null);
  }, []);
  
  // Filtrer par type de prestation
  const handlePrestationFilter = (prestationLabel) => {
    if (selectedPrestation === prestationLabel) {
      // Désélectionner
      setSelectedPrestation(null);
      fetchPrestations(null);
    } else {
      setSelectedPrestation(prestationLabel);
      fetchPrestations(prestationLabel);
    }
  };
  
  // Recherche fullscreen
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchInputRef = useRef(null);
  
  // Charger les recherches récentes
  useEffect(() => {
    try {
      const saved = localStorage.getItem('prestations_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved));
    } catch {}
  }, []);
  
  // Focus sur l'input de recherche
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  // Sauvegarder une recherche
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try {
      localStorage.setItem('prestations_recent_searches', JSON.stringify(updated));
    } catch {}
  };
  
  // Effacer les recherches récentes
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('prestations_recent_searches'); } catch {}
  };
  
  // Soumettre la recherche
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
      const params = new URLSearchParams(searchParams);
      params.set('q', query.trim());
      setSearchParams(params, { replace: true });
      // Recharger avec la recherche
      setSelectedPrestation(null);
      fetchPrestations(query.trim());
    }
    setShowFullscreenSearch(false);
  };
  
  // Clic sur une prestation
  const handlePrestationClick = (prestation) => {
    navigate(`/prestations/${prestation.id}`);
  };
  
  // Gérer le clic sur les onglets
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === 'filtre') {
      setShowFullFilters(true);
    } else if (tab === 'carte') {
      // TODO: Afficher la carte
      navigate('/artisans?view=carte');
    } else if (tab === 'devis') {
      // Ouvrir le formulaire Devis dans Boutiques (même wizard)
      navigate('/artisans?devis=true');
    }
  };
  
  // Filtrer par métier
  const handleMetierFilter = (metierId) => {
    setSelectedMetier(metierId);
    setSelectedPrestation(null); // Reset prestation quand on change de métier
    setShowMetiersMenu(false);
    // L'useEffect va recharger automatiquement
  };
  
  // Filtrer par type de prestation
  const handlePrestationTypeFilter = (prestationType) => {
    setSelectedPrestation(prestationType);
    // Rechercher par le nom de la prestation
    fetchPrestations(selectedMetier, prestationType);
  };
  
  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingBottom: '80px' }}>
      {/* Header Prestations */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center justify-between h-14 px-3">
          <div className="flex items-center gap-0.5">
            <h1 className="text-lg font-bold text-[#1E3A5F]">Prestations</h1>
          </div>
          
          {/* Bouton Loupe */}
          <div className="flex items-center gap-0.5">
            <button 
              onClick={() => setShowFullscreenSearch(true)}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="prestations-search-btn"
            >
              <Search size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
        
        {/* Onglets Tous / Filtre / Devis gratuit */}
        <div className="flex border-b border-gray-100">
          <button 
            onClick={() => handleTabClick('tous')}
            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              activeTab === 'tous' 
                ? 'text-orange-600 border-b-2 border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="prestations-tab-tous"
          >
            <Grid3X3 size={14} />
            Tous
          </button>
          
          <button 
            onClick={() => handleTabClick('filtre')}
            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              activeTab === 'filtre' 
                ? 'text-orange-600 border-b-2 border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="prestations-tab-filtre"
          >
            <SlidersHorizontal size={14} />
            Filtre
          </button>
          
          <button 
            onClick={() => handleTabClick('devis')}
            className="flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-1.5 text-green-600"
            data-testid="prestations-tab-devis"
          >
            <FileText size={14} />
            Devis gratuit
          </button>
        </div>
      </header>
      
      {/* Compteur de résultats */}
      <div className="bg-white px-3 py-2 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{totalPrestations} prestation{totalPrestations > 1 ? 's' : ''}</span>
          {selectedPrestation && (
            <button 
              onClick={() => {
                setSelectedPrestation(null);
                fetchPrestations(null);
              }}
              className="text-xs text-orange-600 font-medium"
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>
      
      {/* Liste des prestations */}
      <div className="p-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            <p className="text-sm text-gray-500 mt-2">Chargement...</p>
          </div>
        ) : prestations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Wrench size={32} className="text-gray-300" />
            </div>
            <h3 className="font-bold text-gray-900">Aucune prestation trouvée</h3>
            <p className="text-sm text-gray-500 mt-1 text-center">Modifie tes critères de recherche</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {prestations.map((prestation) => (
              <PrestationCard 
                key={prestation.id} 
                prestation={prestation}
                onClick={handlePrestationClick}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Recherche Fullscreen - Design identique au reste du site */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-white"
          >
            {/* Bouton fermer */}
            <div className="flex justify-end p-4">
              <button 
                onClick={() => setShowFullscreenSearch(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>
            
            {/* Barre de recherche - Design identique */}
            <div className="px-4">
              <div className="flex border-2 border-orange-500 rounded-full overflow-hidden">
                <button 
                  onClick={() => {
                    if (searchInputRef.current?.value) {
                      handleSearchSubmit(searchInputRef.current.value);
                    }
                  }}
                  className="bg-orange-500 px-5 flex items-center justify-center"
                >
                  <Search size={22} className="text-white" />
                </button>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Prestation, métier, service..."
                  className="flex-1 px-4 py-3.5 text-base focus:outline-none"
                  defaultValue={searchQuery}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchSubmit(e.target.value);
                    }
                  }}
                />
              </div>
              
              {/* Bouton Trouve ta prestation en 3 clics */}
              <button 
                onClick={() => {
                  setShowFullscreenSearch(false);
                  setShowFullFilters(true);
                }}
                className="w-full mt-4 bg-[#1E3A5F] text-white py-3.5 rounded-full flex items-center justify-center gap-2 font-medium"
              >
                <SlidersHorizontal size={18} />
                Trouve ta prestation en 3 clics
              </button>
            </div>
            
            {/* Section recherche */}
            <div className="px-4 mt-6">
              <p className="text-orange-500 font-bold text-sm tracking-wide">RECHERCHE DANS : PRESTATIONS</p>
              <p className="text-gray-400 text-xs font-medium mt-1 tracking-wide">RECHERCHES RÉCENTES</p>
            </div>
            
            {/* Contenu recherches récentes */}
            <div className="flex-1 flex flex-col items-center justify-center px-4 mt-8">
              {recentSearches.length > 0 ? (
                <div className="w-full">
                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map((search, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSearchSubmit(search)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 rounded-full text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                      >
                        <Clock size={14} className="text-gray-400" />
                        {search}
                      </button>
                    ))}
                  </div>
                  <button 
                    onClick={clearRecentSearches} 
                    className="text-xs text-orange-500 font-medium mt-4"
                  >
                    Effacer l'historique
                  </button>
                </div>
              ) : (
                <>
                  <Search size={48} className="text-gray-200 mb-3" />
                  <p className="text-gray-400 text-sm">Aucune recherche récente</p>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Système de filtres */}
      <UnifiedFilterSystem
        context="prestations"
        isOpen={showFullFilters}
        onClose={() => setShowFullFilters(false)}
        filters={advancedFilters}
        onFiltersChange={handleAdvancedFiltersChange}
        resultsCount={totalPrestations}
        onApply={handleApplyAdvancedFilters}
        rayons={[]}
        sousRayons={[]}
        marques={[]}
      />
      
      {/* Bottom Navigation */}
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
    </div>
  );
}
